import React, { useMemo, useState } from 'react';
import { Copy, Check, Eraser, Languages, ArrowLeftRight, CalendarDays, ArrowRight } from 'lucide-react';
import NepaliDate from 'nepali-date-converter';
import { transliterate } from '../utils/transliterator';
import { devanagariToRoman } from '../utils/nepaliRoman';

const NP_MONTHS = ['बैशाख', 'जेठ', 'असार', 'साउन', 'भदौ', 'असोज', 'कार्तिक', 'मंसिर', 'पुष', 'माघ', 'फागुन', 'चैत'];
const AD_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      className={`inline-flex items-center gap-1 text-[12px] font-semibold rounded-full px-3 py-1.5 border transition-colors ${
        copied ? 'bg-green-50 border-green-200 text-green-700' : 'text-muted border-line hover:text-blue hover:border-blue'
      }`}
      onClick={copy}
      disabled={!text}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'कपी भयो' : 'कपी'}
    </button>
  );
}

// ---------- Date converter ----------
const todayAd = () => new Date().toISOString().slice(0, 10);

function adToBs(adStr) {
  if (!adStr) return null;
  const d = new Date(adStr + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  try {
    const nd = new NepaliDate(d);
    const bs = nd.getBS();
    return { y: bs.year, m: bs.month, d: bs.date, monthName: NP_MONTHS[bs.month], weekday: nd.format('ddd', 'np') };
  } catch {
    return null;
  }
}

function bsToAd(y, m, d) {
  if (!y || m === '' || m === undefined || !d) return null;
  try {
    const nd = new NepaliDate(Number(y), Number(m), Number(d));
    const ad = nd.getAD(); // { year, month(0-based), date, day }
    return new Date(ad.year, ad.month, ad.date);
  } catch {
    return null;
  }
}

function DateConverter() {
  const [mode, setMode] = useState('bs2ad');
  const [adDate, setAdDate] = useState(todayAd());
  const [bsY, setBsY] = useState('');
  const [bsM, setBsM] = useState(0);
  const [bsD, setBsD] = useState('');

  // Default BS = aaja ko
  React.useEffect(() => {
    const nd = new NepaliDate(new Date());
    const bs = nd.getBS();
    setBsY(String(bs.year));
    setBsM(bs.month);
    setBsD(String(bs.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bs = useMemo(() => adToBs(adDate), [adDate]);
  const adResult = useMemo(() => bsToAd(bsY, bsM, bsD), [bsY, bsM, bsD]);

  const years = Array.from({ length: 100 }, (_, i) => 2000 + i);

  return (
    <div className="bg-white border border-line rounded-2xl shadow-sm overflow-hidden mt-8">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 sm:px-6 py-4 border-b border-line bg-gradient-to-r from-emerald-50 to-white">
        <span className="flex items-center gap-2 font-display font-bold text-ink">
          <CalendarDays size={18} className="text-green-600" /> मिति परिवर्तक — बि.सं ↔ ई.सं
        </span>
        <div className="flex bg-paper-soft rounded-full p-1 border border-line">
          {[
            ['bs2ad', 'बि.सं → ई.सं'],
            ['ad2bs', 'ई.सं → बि.सं'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setMode(key)}
              className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all ${
                mode === key ? 'bg-crimson text-white shadow' : 'text-muted hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5 sm:p-6">
        {mode === 'bs2ad' ? (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-[1fr_1fr_auto_1.2fr] items-end">
            <label className="flex flex-col gap-1.5 text-[12.5px] font-bold text-ink-soft">
              वर्ष (बि.सं)
              <select value={bsY} onChange={(e) => setBsY(e.target.value)} className="border border-line rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue transition-colors bg-white">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-[12.5px] font-bold text-ink-soft">
              महिना
              <select value={bsM} onChange={(e) => setBsM(Number(e.target.value))} className="border border-line rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue transition-colors bg-white">
                {NP_MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-[12.5px] font-bold text-ink-soft w-full sm:w-24">
              गते
              <input type="number" min={1} max={32} value={bsD} onChange={(e) => setBsD(e.target.value)} className="border border-line rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue transition-colors" />
            </label>
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-line rounded-xl px-4 py-3 min-h-[64px] flex items-center">
              {adResult ? (
                <p className="text-[15px] font-bold text-ink leading-snug">
                  {AD_MONTHS[adResult.getMonth()]} {adResult.getDate()}, {adResult.getFullYear()}
                  <span className="block text-[12px] font-semibold text-muted">{['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][adResult.getDay()]}</span>
                </p>
              ) : (
                <p className="text-[12.5px] text-muted">मिति अमान्य छ — अर्को गते सोध्नुहोस्।</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-[1fr_auto_1.2fr] items-center">
            <input
              type="date"
              value={adDate}
              onChange={(e) => setAdDate(e.target.value)}
              className="border border-line rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue transition-colors"
            />
            <ArrowRight size={18} className="text-muted hidden sm:block" />
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-line rounded-xl px-4 py-3 min-h-[64px] flex items-center">
              {bs ? (
                <p className="text-[15px] font-bold text-ink leading-snug">
                  {NP_MONTHS[bs.m]} {bs.d} गते, {bs.y} — {bs.weekday}
                </p>
              ) : (
                <p className="text-[12.5px] text-muted">मिति छान्नुहोस्।</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Main page ----------
function UnicodePage() {
  const [tab, setTab] = useState('en2np'); // en2np | np2en
  const [input, setInput] = useState('');

  const output = tab === 'en2np' ? transliterate(input) : devanagariToRoman(input);

  return (
    <main className="max-w-[1240px] mx-auto px-5 pt-7 pb-14 w-full">

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue via-crimson to-violet-700 text-white p-7 sm:p-9 mb-8 shadow-lg">
        <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-white/10" />
        <div className="absolute -right-20 bottom-[-70px] w-56 h-56 rounded-full bg-white/10" />
        <h1 className="font-display text-[clamp(24px,3vw,34px)] font-bold mb-2 relative">युनिकोड कन्भर्टर</h1>
        <p className="text-white/85 text-sm max-w-xl leading-relaxed relative">
          नेपाली ↔ English दुवै दिशामा तुरुन्तै रूपान्तरण — र तल मिति परिवर्तक (बि.सं ↔ ई.सं) पनि।
        </p>

        {/* Tab switcher */}
        <div className="relative inline-flex bg-white/15 backdrop-blur-sm rounded-full p-1 mt-5 border border-white/25">
          {[
            ['en2np', 'English → नेपाली'],
            ['np2en', 'नेपाली → English'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setTab(key); setInput(''); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === key ? 'bg-white text-crimson shadow' : 'text-white/85 hover:text-white'
              }`}
            >
              {key === 'np2en' && <ArrowLeftRight size={14} />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Converter panels */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 items-start">
        <div className="bg-white border border-line rounded-2xl shadow-sm overflow-hidden focus-within:border-blue transition-colors">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-line bg-paper-soft">
            <span className="flex items-center gap-2 text-[13px] font-bold text-ink-soft">
              <Languages size={15} /> {tab === 'en2np' ? 'English (Roman)' : 'नेपाली (देवनागरी)'}
            </span>
            <button className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-muted hover:text-crimson transition-colors" onClick={() => setInput('')}>
              <Eraser size={14} /> खाली
            </button>
          </div>
          <textarea
            rows={8}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={tab === 'en2np' ? 'जस्तै: nepalko rajdhani kathmandu ho' : 'जस्तै: काठमाडौं नेपालको राजधानी हो'}
            className="w-full p-5 text-[16px] outline-none resize-y leading-relaxed"
          />
          <div className="px-5 py-3 border-t border-line text-[12px] text-muted">
            {input.length} अक्षर
          </div>
        </div>

        <div className="bg-white border border-line rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-line bg-paper-soft">
            <span className="flex items-center gap-2 text-[13px] font-bold text-ink-soft">
              <span className="w-2 h-2 rounded-full bg-crimson" /> {tab === 'en2np' ? 'नेपाली (देवनागरी)' : 'English (Roman)'}
            </span>
            <CopyBtn text={output} />
          </div>
          <div className="p-5 min-h-[236px]">
            {output ? (
              <p className={`${tab === 'en2np' ? 'font-display text-[22px]' : 'font-mono text-[17px]'} leading-relaxed text-ink break-words`}>{output}</p>
            ) : (
              <p className="text-muted text-sm">यहाँ नतिजा देखिनेछ...</p>
            )}
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-green-50 border border-green-100 rounded-2xl p-5 mt-8">
        <h4 className="text-[14px] font-bold text-ink mb-3">द्रुत टिप्स</h4>
        <ul className="grid gap-2 grid-cols-1 md:grid-cols-2 text-[13px] text-ink-soft">
          <li><span className="font-mono font-semibold">kh → ख</span>, <span className="font-mono font-semibold">gh → घ</span>, <span className="font-mono font-semibold">ch → च</span>, <span className="font-mono font-semibold">chh → छ</span></li>
          <li><span className="font-mono font-semibold">Th → ठ</span>, <span className="font-mono font-semibold">Dh → ढ</span>, <span className="font-mono font-semibold">ph → फ</span>, <span className="font-mono font-semibold">bh → भ</span></li>
          <li><span className="font-mono font-semibold">a → अ</span>, <span className="font-mono font-semibold">aa → ा</span>, <span className="font-mono font-semibold">i → ि</span>, <span className="font-mono font-semibold">ee → ी</span></li>
          <li><span className="font-mono font-semibold">ksh → क्ष</span>, <span className="font-mono font-semibold">gya → ज्ञ</span>, <span className="font-mono font-semibold">tra → त्र</span></li>
        </ul>
      </div>

      {/* BS ↔ AD date converter */}
      <DateConverter />
    </main>
  );
}

export default UnicodePage;